import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  Button,
  InputAdornment,
  Alert,
  Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import LicensingBoardCard from './LicensingBoardCard';
import EditRequirementsForm from './EditRequirementsForm';
import axios from 'axios';
import API_URL from '../../config/config';
import { SetCursorToTop } from '../../utils/utils';
import '../../styles/Licensing-Boards-Landing.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`licensing-tabpanel-${index}`}
      aria-labelledby={`licensing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ paddingTop: 2 }}>{children}</Box>}
    </div>
  );
}

interface BoardData {
  Id: string;
  Name: string;
  Website?: string;
  CME_ProfileCategory_Id: string;
  IsActive?: boolean;
  Active?: boolean;
  requirements?: any[];
}

interface ProcessedBoardData {
  id: string;
  name: string;
  generalCMEHours: string | number;
  topicsCount: number;
  licensurePeriod: string;
  website?: string;
  status: 'active' | 'archived';
  requirerType: string;
  requirements: any[];
}

const LicensingBoardsLanding: React.FC = () => {
  const location = useLocation();

  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'archived'>('all');
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [editingBoardId, setEditingBoardId] = useState<string | null>(null);
  const [pendingTabValue, setPendingTabValue] = useState<number | null>(null);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);

  // Data states
  const [boards, setBoards] = useState<ProcessedBoardData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEditingBoardId(null);
    loadBoardsData();
  }, [location.key]);

  const loadBoardsData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all certification boards with requirements in a SINGLE API call
      const response = await axios.get(`${API_URL}/management/boards-with-requirements`);
      const boardsData: BoardData[] = (response.data as any)?.certificationboards?.rows || [];

      console.log('Loaded boards with requirements:', boardsData.length);
      console.log('Sample board data:', boardsData[0]);

      // Process boards - no need for multiple API calls!
      const processedBoards: ProcessedBoardData[] = boardsData.map((board) => {
        const requirements = (board as any).CME_Requirement || [];

        console.log(`Board ${board.Name} requirements:`, requirements.length);
        if (requirements.length > 0) {
          console.log('Sample requirement:', requirements[0]);
          console.log('Sample requirement CME_Topic:', requirements[0].CME_Topic);
          console.log('Sample requirement full object:', JSON.stringify(requirements[0], null, 2));
        }

        // Calculate general CME hours - check for "general" or "none" topics
        const generalCMEReq = requirements.find((r: any) => {
          const topicName = r.CME_Topic?.Name || r.Topic?.Name || r.TopicName || '';
          console.log(`Checking topic for General CME: "${topicName}"`);
          // Match if topic contains "general" OR is "none" (which we treat as General CME)
          return topicName && (
            topicName.toLowerCase().includes('general') ||
            topicName.toLowerCase() === 'none'
          );
        });

        console.log(`Board ${board.Name} - General CME req:`, generalCMEReq);

        const rawGeneralHours = generalCMEReq
          ? (generalCMEReq.CMEHours != null ? generalCMEReq.CMEHours
            : generalCMEReq.CME_Hours != null ? generalCMEReq.CME_Hours
            : generalCMEReq.Hours != null ? generalCMEReq.Hours
            : 0)
          : null;

        const generalHours = rawGeneralHours === null || rawGeneralHours === 0
          ? '—'
          : `${rawGeneralHours} Hrs`;

        // Calculate licensure period — suppress when hours = 0 (no hourly requirement)
        const period = (generalCMEReq && rawGeneralHours !== 0 && rawGeneralHours !== null && generalCMEReq.Frequency)
          ? `${generalCMEReq.Frequency}`
          : '—'; 

        // Process requirements for display
        const processedRequirements = requirements.map((req: any) => {
          const topicName = req.CME_Topic?.Name || req.Topic?.Name || req.TopicName || '';
          // Check multiple possible field names for isCreditType
          const isCreditType = req.CME_Topic?.isCreditType || req.CME_Topic?.IsCreditType ||
            req.Topic?.isCreditType || req.Topic?.IsCreditType || false;
          console.log(`Processing requirement - Topic Name: "${topicName}", Hours: ${req.CMEHours}, isCreditType: ${isCreditType}, Topic object:`, req.CME_Topic);

          return {
            id: req.Id,
            topic: topicName && topicName.toLowerCase() !== 'none' ? topicName : 'General CME',
            hours: req.CMEHours || req.CME_Hours || req.Hours || 0,
            description: req.Description,
            frequency: req.Frequency,
            interval: req.Interval,
            statute: req.Statute,
            notes: req.Notes,
            isCreditType: isCreditType,
            lastUpdated: req.UpdatedAt
              ? new Date(req.UpdatedAt).toLocaleDateString()
              : undefined
          };
        }).sort((a: any, b: any) => {
          // Sort so General CME appears first
          const aIsGeneralCME = a.topic.toLowerCase().includes('general');
          const bIsGeneralCME = b.topic.toLowerCase().includes('general');

          if (aIsGeneralCME && !bIsGeneralCME) return -1;
          if (!aIsGeneralCME && bIsGeneralCME) return 1;
          return 0; // Keep original order for other topics
        });

        // Determine if board is active or archived
        // Check multiple possible field names for active status
        let isActive = true; // Default to active
        const boardAny = board as any;

        if (board.IsActive !== undefined) {
          isActive = board.IsActive === true;
        } else if (board.Active !== undefined) {
          isActive = board.Active === true;
        } else if (boardAny.IsActive !== undefined) {
          isActive = boardAny.IsActive === true || boardAny.IsActive === 1;
        } else if (boardAny.Active !== undefined) {
          isActive = boardAny.Active === true || boardAny.Active === 1;
        } else if (boardAny.Status !== undefined) {
          isActive = boardAny.Status === 'Active' || boardAny.Status === 1;
        } else if (boardAny.IsDeleted !== undefined) {
          isActive = !boardAny.IsDeleted;
        }

       

        const status: 'active' | 'archived' = isActive ? 'active' : 'archived';
        const categoryName = (board as any).CME_ProfileCategory?.Name || '';

        console.log(`Board ${board.Name} (${board.Id}) - Category: ${categoryName}, Active: ${isActive}, Status: ${status}`);

        return {
          id: board.Id,
          name: board.Name,
          generalCMEHours: generalHours,
          topicsCount: processedRequirements.filter((r: any) => !r.isCreditType && r.topic.toLowerCase() !== 'general cme').length,
          licensurePeriod: period,
          website: board.Website,
          status: status,
          requirerType: categoryName,
          requirements: processedRequirements
        };
      });

      console.log('Processed boards:', processedBoards.length);
      console.log('Active boards:', processedBoards.filter(b => b.status === 'active').length);
      console.log('Archived boards:', processedBoards.filter(b => b.status === 'archived').length);
      console.log('State Medical Board:', processedBoards.filter(b => b.requirerType === 'State Medical Board').length);
      console.log('Medical Specialty Board:', processedBoards.filter(b => b.requirerType === 'Medical Specialty Board').length);

      setBoards(processedBoards);
    } catch (err: any) {
      console.error('Error loading boards:', err);
      setError('Failed to load licensing boards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (editingBoardId) {
      setPendingTabValue(newValue);
      setLeaveDialogOpen(true);
      return;
    }
    setTabValue(newValue);
    setExpandedCardId(null);
    setFilterStatus('all');
    setSearchQuery('');
  };

  const handleLeaveConfirm = () => {
    setEditingBoardId(null);
    SetCursorToTop();
    setLeaveDialogOpen(false);
    if (pendingTabValue !== null) {
      setTabValue(pendingTabValue);
      setPendingTabValue(null);
      setExpandedCardId(null);
      setFilterStatus('all');
      setSearchQuery('');
    }
  };

  const handleLeaveCancel = () => {
    setLeaveDialogOpen(false);
    setPendingTabValue(null);
  };

  const handleCardExpand = (id: string) => {
    setExpandedCardId(expandedCardId === id ? null : id);
  };

  const handleCardEdit = (id: string) => {
    setEditingBoardId(id);
  };

  const handleAddLicensingBoard = () => {
    // TODO: Open dialog to add new licensing board
    console.log('Add new licensing board');
  };

  // Filter boards by tab (requirer type) and search query
  const getFilteredBoards = () => {
    let filtered = boards;

    // Filter by tab (requirer type)
    if (tabValue === 0) {
      // State Medical Board
      filtered = filtered.filter(b => b.requirerType === 'State Medical Board');
    } else {
      // Medical Specialty Board
      filtered = filtered.filter(b => b.requirerType === 'Medical Specialty Board');
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter((b) => b.status === filterStatus);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter((b) =>
        b.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered.sort((a, b) => a.name.localeCompare(b.name));
  };

  const filteredBoards = getFilteredBoards();

  // Calculate counts for filter buttons based on current tab
  const currentTabBoards = boards.filter((b) => {
    if (tabValue === 0) {
      return b.requirerType === 'State Medical Board';
    } else {
      return b.requirerType === 'Medical Specialty Board';
    }
  });

  const allCount = currentTabBoards.length;
  const activeCount = currentTabBoards.filter((b) => b.status === 'active').length;
  const archivedCount = currentTabBoards.filter((b) => b.status === 'archived').length;

  return (
    <Box className="licensing-boards-landing-container" sx={{ marginTop: '0px' }}>
      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '0px',
          borderBottom: '1px solid #E0E0E0',
          backgroundColor: '#FFFFFF',
          marginTop: '0px'
        }}
      >
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '18px',
              minHeight: '56px',
              padding: '12px 24px',
              '&:hover': { backgroundColor: 'transparent' },
              '&.Mui-focusVisible': { backgroundColor: 'transparent' }
            },
            '& .Mui-selected': {
              color: '#1976D2'
            }
          }}
        >
          <Tab label="State Medical Board" disableRipple />
          <Tab label="Medical Specialty Board" disableRipple />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {editingBoardId ? (
          <EditRequirementsForm
            boardId={editingBoardId}
            onCancel={handleLeaveConfirm}
            onSave={() => { setEditingBoardId(null); SetCursorToTop(); loadBoardsData(); }}
            leaveRequested={leaveDialogOpen}
            onLeaveHandled={handleLeaveCancel}
          />
        ) : (
        <Box sx={{ padding: '24px', backgroundColor: '#FFFFFF' }}>
          {/* Filter and Search Section */}
          <Box
            className="licensing-boards-filter-section"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              '@media (min-width: 768px)': {
                flexWrap: 'nowrap'
              },
              gap: '16px'
            }}
          >
            {/* Left Side: Status Filters and Search in same row */}
            <Box sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              '@media (min-width: 768px)': {
                flexWrap: 'nowrap'
              },
              flex: 1
            }}>
              {/* Status Filters */}
              <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <Chip
                  label={`All ${allCount}`}
                  onClick={() => setFilterStatus('all')}
                  sx={{
                    backgroundColor: filterStatus === 'all' ? '#E3F2FD' : '#F5F5F5',
                    color: filterStatus === 'all' ? '#1976D2' : '#666',
                    fontWeight: filterStatus === 'all' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: filterStatus === 'all' ? '#BBDEFB' : '#EEEEEE'
                    }
                  }}
                />
                <Chip
                  label={`Active ${activeCount}`}
                  onClick={() => setFilterStatus('active')}
                  sx={{
                    backgroundColor: filterStatus === 'active' ? '#E3F2FD' : '#F5F5F5',
                    color: filterStatus === 'active' ? '#1976D2' : '#666',
                    fontWeight: filterStatus === 'active' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: filterStatus === 'active' ? '#BBDEFB' : '#EEEEEE'
                    }
                  }}
                />
                <Chip
                  label={`Archived ${archivedCount}`}
                  onClick={() => setFilterStatus('archived')}
                  sx={{
                    backgroundColor: filterStatus === 'archived' ? '#E3F2FD' : '#F5F5F5',
                    color: filterStatus === 'archived' ? '#1976D2' : '#666',
                    fontWeight: filterStatus === 'archived' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: filterStatus === 'archived' ? '#BBDEFB' : '#EEEEEE'
                    }
                  }}
                />
              </Box>

              {/* Search Box - inline with filters */}
              <TextField
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <ClearIcon onClick={() => setSearchQuery('')} style={{ cursor: 'pointer', fontSize: '18px', color: '#999' }} />
                    </InputAdornment>
                  ) : null
                }}
                inputProps={{
                  className: 'no-border-input'
                }}
                sx={{
                  minWidth: { xs: '100%', sm: '250px' },
                  flex: 1,
                  maxWidth: { md: '450px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '50px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderColor: '#DDDDDD'
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976D2'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976D2'
                    }
                  },
                  '& input': {
                    cursor: 'text !important'
                  }
                }}
              />
            </Box>

            {/* Right Side: Add Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddLicensingBoard}
              disabled
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                borderRadius: '50px',
                padding: '8px 24px',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              Licensing Board
            </Button>
          </Box>



          {/* Loading State */}
          {loading && (
            <div className="spinner"></div>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          )}

          {/* Board Cards */}
          {!loading && !error && (
            <>
              {filteredBoards.length === 0 ? (
                <Alert severity="info">
                  No licensing boards found. {searchQuery ? 'Try adjusting your search.' : ''}
                </Alert>
              ) : (
                <>
                  {/* Table Header Row */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '3fr 0.8fr 0.8fr 1fr 1.6fr',
                        md: '3fr 0.8fr 0.8fr 1fr 1.6fr 60px'
                      },
                      gap: '16px',                      
                      padding: '0px 20px 0px 0px',
                      backgroundColor: '#FFFFFF',                      
                      marginBottom: '8px'
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black', textAlign: 'left' }}>
                      Licensing Board Name
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}>
                      General CME
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}>
                      # of Topics
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}>
                      Licensure Period
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black', textAlign: 'left' }}>
                      Website
                    </Typography>
                    <Box></Box>
                  </Box>

                  {/* Cards */}
                  <Box className="licensing-boards-grid">
                    {filteredBoards.map((board) => (
                      <LicensingBoardCard
                        key={board.id}
                        id={board.id}
                        name={board.name}
                        generalCMEHours={board.generalCMEHours}
                        topicsCount={board.topicsCount}
                        licensurePeriod={board.licensurePeriod}
                        website={board.website}
                        status={board.status}
                        requirements={board.requirements}
                        isExpanded={expandedCardId === board.id}
                        onExpand={handleCardExpand}
                        onEdit={handleCardEdit}
                      />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {editingBoardId ? (
          <EditRequirementsForm
            boardId={editingBoardId}
            onCancel={handleLeaveConfirm}
            onSave={() => { setEditingBoardId(null); SetCursorToTop(); loadBoardsData(); }}
            leaveRequested={leaveDialogOpen}
            onLeaveHandled={handleLeaveCancel}
          />
        ) : (
        <Box sx={{ padding: '24px', backgroundColor: '#FFFFFF' }}>
          {/* Same content as Tab 0 - will show different boards based on type */}
          {/* Filter and Search Section */}
          <Box
            className="licensing-boards-filter-section"
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              '@media (min-width: 768px)': {
                flexWrap: 'nowrap'
              },
              gap: '16px'
            }}
          >
            {/* Left Side: Status Filters and Search in same row */}
            <Box sx={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              '@media (min-width: 768px)': {
                flexWrap: 'nowrap'
              },
              flex: 1
            }}>
              {/* Status Filters */}
              <Box sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <Chip
                  label={`All ${allCount}`}
                  onClick={() => setFilterStatus('all')}
                  sx={{
                    backgroundColor: filterStatus === 'all' ? '#E3F2FD' : '#F5F5F5',
                    color: filterStatus === 'all' ? '#1976D2' : '#666',
                    fontWeight: filterStatus === 'all' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: filterStatus === 'all' ? '#BBDEFB' : '#EEEEEE'
                    }
                  }}
                />
                <Chip
                  label={`Active ${activeCount}`}
                  onClick={() => setFilterStatus('active')}
                  sx={{
                    backgroundColor: filterStatus === 'active' ? '#E3F2FD' : '#F5F5F5',
                    color: filterStatus === 'active' ? '#1976D2' : '#666',
                    fontWeight: filterStatus === 'active' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: filterStatus === 'active' ? '#BBDEFB' : '#EEEEEE'
                    }
                  }}
                />
                <Chip
                  label={`Archived ${archivedCount}`}
                  onClick={() => setFilterStatus('archived')}
                  sx={{
                    backgroundColor: filterStatus === 'archived' ? '#E3F2FD' : '#F5F5F5',
                    color: filterStatus === 'archived' ? '#1976D2' : '#666',
                    fontWeight: filterStatus === 'archived' ? 600 : 400,
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: filterStatus === 'archived' ? '#BBDEFB' : '#EEEEEE'
                    }
                  }}
                />
              </Box>

              {/* Search Box - inline with filters */}
              <TextField
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#999' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <ClearIcon onClick={() => setSearchQuery('')} style={{ cursor: 'pointer', fontSize: '18px', color: '#999' }} />
                    </InputAdornment>
                  ) : null
                }}
                inputProps={{
                  className: 'no-border-input'
                }}
                sx={{
                  minWidth: { xs: '100%', sm: '250px' },
                  flex: 1,
                  maxWidth: { md: '450px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '50px',
                    backgroundColor: '#FFFFFF',
                    '& fieldset': {
                      borderColor: '#DDDDDD'
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976D2'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976D2'
                    }
                  },
                  '& input': {
                    cursor: 'text !important'
                  }
                }}
              />
            </Box>

            {/* Right Side: Add Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddLicensingBoard}
              disabled
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                borderRadius: '50px',
                padding: '8px 24px',
                fontWeight: 600,
                whiteSpace: 'nowrap'
              }}
            >
              Licensing Board
            </Button>
          </Box>



          {/* Loading State */}
          {loading && (
            <div className="spinner"></div>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          )}

          {/* Board Cards */}
          {!loading && !error && (
            <>
              {filteredBoards.length === 0 ? (
                <Alert severity="info">
                  No licensing boards found. {searchQuery ? 'Try adjusting your search.' : ''}
                </Alert>
              ) : (
                <>
                  {/* Table Header Row */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '3fr 0.8fr 0.8fr 1fr 1.6fr 60px',
                      gap: '16px',
                      padding: '0px 20px 0px 0px',
                      backgroundColor: '#FFFFFF',
                      
                      marginBottom: '8px'
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black', textAlign: 'left' }}>
                      Licensing Board Name
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}>
                      General CME
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}>
                      # of Topics
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black' }}>
                      Licensure Period
                    </Typography>
                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: 'black', textAlign: 'left' }}>
                      Website
                    </Typography>
                    <Box></Box>
                  </Box>

                  {/* Cards */}
                  <Box className="licensing-boards-grid">
                    {filteredBoards.map((board) => (
                      <LicensingBoardCard
                        key={board.id}
                        id={board.id}
                        name={board.name}
                        generalCMEHours={board.generalCMEHours}
                        topicsCount={board.topicsCount}
                        licensurePeriod={board.licensurePeriod}
                        website={board.website}
                        status={board.status}
                        requirements={board.requirements}
                        isExpanded={expandedCardId === board.id}
                        onExpand={handleCardExpand}
                        onEdit={handleCardEdit}
                      />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
        )}
      </TabPanel>

    </Box>
  );
};

export default LicensingBoardsLanding;
